import { Formik } from 'formik';
import * as Yup from 'yup';
import { IAdminDownloadAllPolicies } from '../../resources/interfaces';
import { POLICY_REVIEW_STATES } from '../../resources/enums';
import DatePickerInput from '../form-control/DatePickerInput';
import { subDays } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Button from '../form-control/Button';
import moment from 'moment';
import { INPUT_DATE_FORMAT } from '../../resources/constants';
import { downloadPolicies } from '../../api/requests';
import { useState } from 'react';
import { capitalizeSentence, showToast } from '../../utils/miscFunctions';
import SelectDropdown from '../form-control/SelectDropdown';
import useUserAccount from '../../hooks/userAccountHook';
import { User } from '../../models/User';

interface IDownloadPromptProps {
  onProceed: (data: IAdminDownloadAllPolicies) => void;
  onClose: () => void;
  reviewStage?: POLICY_REVIEW_STATES;
  branchStaffs?: any[];
}

export interface IFormData {
  active: boolean;
  reviewStage: string;
  from: string;
  to: string;
}

const AdminDownloadAllPolicies = ({ onProceed, onClose, reviewStage, branchStaffs }: IDownloadPromptProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getProfile } = useUserAccount();
  const user = new User(getProfile());

  const submitHandler = (values: any) => {
    setSubmitting(true);

    const data: IFormData = {
      ...values,
      active: values.active,
      reviewStage: user.isOwner()
        ? values.reviewStage == 'All'
          ? null
          : values.reviewStage
        : 'APPROVED',
      from: moment(values.from).format(INPUT_DATE_FORMAT),
      to: moment(values.to).format(INPUT_DATE_FORMAT),
      managerId: values.managerId
    };

    downloadPolicies(data)
      .then((response: any) => {
        window.open(response.documentUrl, '_blank');
        onProceed(data);
      })
      .catch((response: any) => {
        showToast.error(response?.description);
      })
      .finally(() => setSubmitting(false));
  };

  const revienStageList = [POLICY_REVIEW_STATES.APPROVED, POLICY_REVIEW_STATES.EXPIRED].map((stage)=> ({
    name: stage === POLICY_REVIEW_STATES.APPROVED ? 'Active' : capitalizeSentence(stage),
    value: stage
  }))

  return (
    <>
      <Formik
        initialValues={{
          active: true,
          reviewStage: reviewStage,
          from: subDays(new Date(), 7),
          to: new Date(),
          managerId: undefined,
        }}
        validateOnBlur
        validationSchema={Yup.object().shape({
          reviewStage: user.isOwner()
            ? Yup.string().required('Review stage is required')
            : Yup.string(),
          from: Yup.date().typeError('Please pick a valid "from" date').required('Please pick a "from" date')
        })}
        onSubmit={(values) => {
          submitHandler(values);
        }}>
        {({
          values,
          errors,
          isSubmitting,
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue,
          touched
        }) => (
          <form className="px-4 py-[30px] bg-white dark:bg-darkBg space-y-4 rounded-xl" onSubmit={handleSubmit}>
         
            <div className="flex space-x-4 w-full justify-between">
             
              <DatePickerInput
                id={'from'}
                label="From"
                selected={values.from}
                onChange={(date: Date | null) => {
                  setFieldValue('from', date);
                }}
                classes="w-full"
                showYearDropdown
                error={touched.from && errors.from !== undefined ? String(errors.from) : undefined}
                helperText={
                  touched.from && errors.from !== undefined ? String(errors.from) : undefined
                }
                dateFormat={'dd/MM/yyyy'}
              />

              <DatePickerInput
                id={'to'}
                selected={values.to}
                onChange={(date: Date | null) => {
                  setFieldValue('to', date);
                }}
                label="To"
                classes="w-full"
                showYearDropdown
                error={touched.to && errors.to !== undefined ? String(errors.to) : undefined}
                helperText={touched.to && errors.to !== undefined ? String(errors.to) : undefined}
                dateFormat={'dd/MM/yyyy'}
              />
            </div>
            {user.isOwner() && !reviewStage && (
              <SelectDropdown
                name="reviewStage"
                label="Policies Status"
                listSelectedValue={values.reviewStage}
                onChange={(value: string) => {
                  setFieldValue('reviewStage', value);
                }}
                list={revienStageList}
                error={touched.reviewStage ? errors.reviewStage : undefined}
                helperText={touched.reviewStage ? errors.reviewStage : undefined}
              />
            )}

            {!user.isAdministrator() && !user.isAgent() && (
              <SelectDropdown
                name="managerId"
                label="Select manager"
                listSelectedValue={values.managerId}
                onChange={(value: string) => {
                  setFieldValue('managerId', value);
                }}
                list={branchStaffs}
                // error={touched.staff ? errors.staff : undefined}
                // helperText={touched.staff ? errors.staff : undefined}
              />
            )}

             <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={onClose}
                  bgColor='secondary'
                  fullWidth
                  disabled={submitting}
                />
                <Button
                  label="Submit"
                  onClick={handleSubmit}
                  bgColor='primary'
              fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AdminDownloadAllPolicies;
